var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Mediator_1;
import { injectable } from '../decorators/injectable';
/** The name of the module */
export const name = 'Mediator';
let Mediator = Mediator_1 = class Mediator {
    /**
       * Build a namespaced event name
       *
       * @param namespace Namespace to use for the event name
       * @param eventName Event to use for the event name (e.g. 'click')
       */
    static getNamespacedEvent(namespace, eventName) {
        return `durango:${namespace}:${eventName}`;
    }
    /**
       * Register an event for mediation
       *
       * @param namespace Namespace to use for the event name
       * @param eventName Event to use for the event name (e.g. 'click')
       */
    static register(namespace, eventName) {
        // Get the fully namespaced event name
        const namespacedEventName = Mediator_1.getNamespacedEvent(namespace, eventName);
        // If the event hasn't been registered yet, then register the event and initialize the listeners array
        if (!Mediator_1.events.has(namespacedEventName)) {
            Mediator_1.events.add(namespacedEventName);
            Mediator_1.listeners.set(namespacedEventName, []);
        }
    }
    /**
       * Fire a mediated event for any callback functions registered as listeners to handle
       *
       * @param namespace Namespace to use for the event name
       * @param eventName Event to use for the event name (e.g. 'click')
       * @param eventData Custom data to be included with the event
       */
    static fire(namespace, eventName, eventData) {
        // Get the fully namespaced event name
        const namespacedEventName = Mediator_1.getNamespacedEvent(namespace, eventName);
        // Check that the event has actually been registered
        if (!Mediator_1.events.has(namespacedEventName)) {
            throw Error(`Unable to fire event that has not been registered: ${eventName} in ${namespace} namespace`);
        }
        // Fetch the callbacks to notify
        const callbacks = Mediator_1.listeners.get(namespacedEventName);
        // Create the custom event to fire
        const event = Mediator_1.createMediatorEvent(namespacedEventName, eventData);
        // Execute each callback registered for the mediated event
        callbacks.forEach((callback) => callback(event));
    }
    /**
       * Register a callback function for a specific event
       *
       * @param namespace Namespace to use for the event name
       * @param eventName Event to use for the event name (e.g. 'click')
       * @param callback Callback function for when event is fired
       */
    static addListener(namespace, eventName, callback) {
        // Get the fully namespaced event name
        const namespacedEventName = Mediator_1.getNamespacedEvent(namespace, eventName);
        // Add the callback
        Mediator_1.listeners.get(namespacedEventName).push(callback);
    }
    /**
       * Remove a callback function for a specific event
       *
       * @param namespace Namespace for the event name
       * @param eventName Event to use for the event name (e.g. 'click')
       * @param callback Callback function for when the event is fired
       */
    static removeListener(namespace, eventName, callback) {
        // Get the fully namespaced event name
        const namespacedEventName = Mediator_1.getNamespacedEvent(namespace, eventName);
        // Build a new array of listeners excluding the specified callback function
        const filteredListeners = Mediator_1.listeners.get(namespacedEventName).filter((fn) => fn !== callback);
        // Update the listeners with the filtered callbacks
        Mediator_1.listeners.set(namespacedEventName, filteredListeners);
    }
    /**
       * Create custom event to be passed to callback function
       *
       * @param eventName Namespaced event name to create event object for
       * @param eventData Custom data to be sent with the event
       */
    static createMediatorEvent(eventName, eventData) {
        const options = {
            detail: eventData || null,
        };
        return new CustomEvent(eventName, options);
    }
};
/** Events registered for mediation */
Mediator.events = new Set();
/** Event listeners registered for handling mediated events */
Mediator.listeners = new Map();
Mediator = Mediator_1 = __decorate([
    injectable(name)
], Mediator);
export default Mediator;
